// Phase 4-D6 GREEN — b63Kr 新建私信独立 route
//
// frame_id=b63Kr @ ui/dogdisk-user.pen media-msg-ticket.md §b63Kr
// route=`/messages/new`(独立 route;非 modal — 用户搜索复杂度需独立页面)
// 触发:`/messages`(p53Mb)newMsgBtn(FgMb8)→ navigate('/messages/new')
//
// 流程:
//   1. 用户搜索 input(精确 UUID;USER_NOT_FOUND 兜底)
//   2. 点击命中行选 target user
//   3. 写消息 textarea + submit
//   4. submit:POST /gapi/user/conversations(已存则幂等 is_new=false)
//      → POST /gapi/user/conversations/:id/messages
//      → 跳 /messages/:conversation_id(连续聊天)
//
// 复用既有 endpoint(per spec §7 默认决策):
//   - POST /gapi/user/conversations(messaging_handlers.go:300)
//   - POST /gapi/user/conversations/:id/messages(messaging_handlers.go:216)
//
// 边界:
//   - 用户搜索当前 MVP 走精确 UUID(GET /gapi/public/users/:id 4 视角投影 anon 字段);
//     模糊搜索(用户名)待 P3 实装 search index;
//   - emptyState "USER_NOT_FOUND" 文案对齐 errcode 名(spec §4.5)
//
// data-testid 约定:
//   - 根:b63Kr
//   - 搜索 input:b63Kr-search
//   - 命中行:b63Kr-target-row(单行;模糊搜索后改 b63Kr-row-{user_id})
//   - 消息 textarea:b63Kr-msg
//   - submit / cancel:b63Kr-submit / b63Kr-cancel

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Spinner, apiClient, ApiClientError, getSupabase } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'
import { resourceUrl } from '../../_lib/resource-node-pref'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type SearchedUser = {
  user_id: string
  username: string | null
  avatar_url: string | null
}

export function MessageNewPage() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [target, setTarget] = useState<SearchedUser | null>(null)
  const [searchErr, setSearchErr] = useState<string | null>(null)
  const [searching, setSearching] = useState(false)
  const [msg, setMsg] = useState('')

  const onSearch = async (v: string) => {
    setQuery(v)
    setTarget(null)
    setSearchErr(null)
    const trimmed = v.trim()
    if (!UUID_RE.test(trimmed)) {
      // 不是合法 UUID → 暂不调远端;输入完整 UUID 才触发
      return
    }
    setSearching(true)
    try {
      // GET /gapi/public/users/:user_id 4 视角投影(anon 视角返 username / avatar_url)
      const data = await apiClient.get<{ user: SearchedUser }>(`/gapi/public/users/${trimmed}`)
      setTarget(data.user ?? null)
      if (!data.user) {
        setSearchErr('USER_NOT_FOUND')
      }
    } catch (e) {
      if (e instanceof ApiClientError) {
        setSearchErr(e.message || 'USER_NOT_FOUND')
      } else {
        setSearchErr(e instanceof Error ? e.message : 'USER_NOT_FOUND')
      }
    } finally {
      setSearching(false)
    }
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error('请先选择对方用户')
      if (!msg.trim()) throw new Error('请输入消息内容')
      // step1:POST create_private_conversation(幂等)
      const conv = await apiClient.post<{ conv_id: string; is_new: boolean }>(
        '/gapi/user/conversations',
        { target_user_id: target.user_id },
      )
      // step2:POST send_message(conversation_id 走 path)
      await apiClient.post(`/gapi/user/conversations/${conv.conv_id}/messages`, {
        content: msg.trim(),
        attachments: [],
      })
      return conv.conv_id
    },
    onSuccess: (convID) => {
      navigate(`/messages/${convID}`)
    },
  })

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <div data-testid="b63Kr" className="flex flex-1 flex-col">
          <TopBar title="新建私信" />

          <section className="bg-white px-6 py-3">
            <label className="block pb-1 text-[12px] text-[#9CA3AF]" htmlFor="b63Kr-search">
              输入对方 user_id(UUID 精确)
            </label>
            <input
              id="b63Kr-search"
              data-testid="b63Kr-search"
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="w-full rounded-lg bg-[#F6F7F8] px-3 py-2 font-mono text-[12px] outline-none placeholder:text-[#9CA3AF]"
            />
            {searching && (
              <div className="mt-2 flex items-center gap-2 text-[12px] text-[#9CA3AF]">
                <Spinner className="h-4 w-4" /> 搜索中…
              </div>
            )}
            {searchErr && !searching && (
              <p className="mt-2 text-[12px] text-[#FF6B6B]">未找到该用户({searchErr})</p>
            )}
          </section>

          {target && (
            <section className="bg-white px-6 py-3">
              <p className="pb-1 text-[12px] text-[#9CA3AF]">已选对方</p>
              <button
                type="button"
                data-testid="b63Kr-target-row"
                className="flex w-full items-center gap-3 rounded-lg bg-[#F6F7F8] px-3 py-2 text-left"
                onClick={() => {
                  /* 单行选中,already selected;click 同样保留(测试 click 行为)*/
                }}
              >
                <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-[#E5E7EB] text-[12px] font-semibold text-[#6B7280]">
                  {target.avatar_url ? (
                    <img src={resourceUrl(target.avatar_url)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    (target.username ?? target.user_id).slice(0, 1).toUpperCase()
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-[#1A1A1A]">
                    {target.username ?? '匿名用户'}
                  </span>
                  <span className="block truncate font-mono text-[11px] text-[#9CA3AF]">
                    {target.user_id}
                  </span>
                </span>
              </button>
            </section>
          )}

          <section className="flex-1 bg-white px-6 py-3">
            <label className="block pb-1 text-[12px] text-[#9CA3AF]" htmlFor="b63Kr-msg">
              消息内容
            </label>
            <textarea
              id="b63Kr-msg"
              data-testid="b63Kr-msg"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={4}
              placeholder="输入首发消息(≤4000 字)"
              className="w-full resize-none rounded-lg bg-[#F6F7F8] p-3 text-[13px] outline-none placeholder:text-[#9CA3AF]"
            />
            {mutation.isError && (
              <p className="mt-2 text-[12px] text-[#FF6B6B]">
                {(mutation.error as Error)?.message ?? '发送失败'}
              </p>
            )}
          </section>

          <footer
            className="flex gap-2 border-t border-[#F3F4F6] bg-white px-6 pt-3"
            style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
          >
            <button
              type="button"
              data-testid="b63Kr-cancel"
              onClick={() => navigate('/messages')}
              className="h-11 flex-1 rounded-lg bg-[#F1F2F4] text-[14px] font-semibold text-[#1A1A1A]"
            >
              取消
            </button>
            <button
              type="button"
              data-testid="b63Kr-submit"
              disabled={mutation.isPending || !target || !msg.trim()}
              onClick={() => mutation.mutate()}
              className="h-11 flex-1 rounded-lg bg-[#19caad] text-[14px] font-semibold text-white disabled:opacity-50"
            >
              {mutation.isPending ? '发送中…' : '发送'}
            </button>
          </footer>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}

// keep import warning silent for ESM-strict envs; getSupabase reserved for future fuzzy search.
void getSupabase
